import React from 'react';

// Minimal markdown renderer for AI answers (headings, bullet lists, tables,
// **bold**) - small enough to avoid pulling in a markdown dependency.
//
// Bold is parsed by toggling on every '**' instead of matching closed pairs,
// so text that is still streaming in ("**Video" with no closing marker yet)
// shows as bold instead of flashing literal asterisks.
//
// `sourceTags` additionally styles "[Chart Name]" citations, which only the
// Combined Summary uses; leave it off for ordinary answers, where square
// brackets are just text.
export function tokenizeInline(text, { sourceTags = false } = {}) {
  const tokens = [];
  String(text).split('**').forEach((segment, idx) => {
    if (!segment) return;
    if (idx % 2 === 1) {
      tokens.push({ type: 'bold', text: segment });
      return;
    }
    if (!sourceTags) {
      tokens.push({ type: 'text', text: segment });
      return;
    }
    segment.split(/(\[[^\]]+\])/g).forEach((part) => {
      if (!part) return;
      tokens.push({
        type: /^\[[^\]]+\]$/.test(part) ? 'tag' : 'text',
        text: part,
      });
    });
  });
  return tokens;
}

function renderInline(text, keyPrefix, options) {
  return tokenizeInline(text, options).map((token, idx) => {
    const key = `${keyPrefix}-${idx}`;
    if (token.type === 'bold') return <strong key={key}>{token.text}</strong>;
    if (token.type === 'tag') {
      return (
        <span key={key} className="text-xs text-primary font-medium">
          {token.text}
        </span>
      );
    }
    return <React.Fragment key={key}>{token.text}</React.Fragment>;
  });
}

export function renderMarkdown(text, options) {
  if (!text) return null;
  const lines = String(text).split('\n');
  const blocks = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (/^\s*\|/.test(line)) {
      const tableLines = [];
      while (i < lines.length && /^\s*\|/.test(lines[i])) {
        tableLines.push(lines[i]);
        i += 1;
      }
      const rows = tableLines
        .filter((l) => !/^\s*\|[\s-:|]+\|\s*$/.test(l))
        .map((l) =>
          l
            .trim()
            .replace(/^\||\|$/g, '')
            .split('|')
            .map((c) => c.trim()),
        );
      blocks.push(
        <table key={blocks.length} className="w-full text-sm border-collapse my-2">
          <tbody>
            {rows.map((cells, ri) => (
              <tr key={ri} className={ri === 0 ? 'font-semibold bg-gray-50' : 'border-t border-gray-100'}>
                {cells.map((c, ci) => (
                  <td key={ci} className="px-2 py-1">
                    {renderInline(c, `t${blocks.length}-${ri}-${ci}`, options)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>,
      );
      continue;
    }
    const headingMatch = line.match(/^(#{1,4})\s+(.*)$/);
    if (headingMatch) {
      const level = Math.min(headingMatch[1].length + 2, 6);
      const Tag = `h${level}`;
      blocks.push(
        React.createElement(
          Tag,
          { key: blocks.length, className: 'font-semibold mt-3 mb-1' },
          renderInline(headingMatch[2], `h${blocks.length}`, options),
        ),
      );
      i += 1;
      continue;
    }
    if (/^\s*[-*]\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*[-*]\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*[-*]\s+/, ''));
        i += 1;
      }
      blocks.push(
        <ul key={blocks.length} className="list-disc pl-5 my-1 space-y-0.5">
          {items.map((it, ii) => (
            <li key={ii}>{renderInline(it, `l${blocks.length}-${ii}`, options)}</li>
          ))}
        </ul>,
      );
      continue;
    }
    if (line.trim() === '') {
      i += 1;
      continue;
    }
    blocks.push(
      <p key={blocks.length} className="my-1">
        {renderInline(line, `p${blocks.length}`, options)}
      </p>,
    );
    i += 1;
  }
  return blocks;
}
